/**
 * Hala AI Learning System - Sessions Page
 * Integrated: Chat UI + Message Evaluation + Decision Trace + Intelligence Panel
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { 
  Search, MessageSquare, User, Clock, CheckCircle, AlertTriangle,
  X, ChevronDown, Play, Pause, RefreshCw, Eye, Brain, Target,
  Zap, TrendingUp, AlertCircle, Check, XCircle, Edit2, Trash2,
  Send, ThumbsUp, ThumbsDown, HelpCircle, BookOpen, Sparkles,
  ExternalLink
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auth helper
const getAuthHeaders = () => {
  const token = localStorage.getItem('hala_admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ═══════════════════════════════════════════════════════════════════════════════
// DESIGN SYSTEM
// ═══════════════════════════════════════════════════════════════════════════════

const COLORS = {
  primary: '#1B3A6B',
  primaryLight: '#2A4A7A',
  gold: '#C5A572',
  goldLight: '#D4B896',
  success: '#10B981',
  successLight: '#D1FAE5',
  warning: '#F59E0B',
  warningLight: '#FEF3C7',
  error: '#EF4444',
  errorLight: '#FEE2E2',
  info: '#3B82F6',
  infoLight: '#DBEAFE',
  white: '#FFFFFF',
  bg: '#F8FAFC',
  bgDark: '#F1F5F9',
  border: '#E2E8F0',
  text: '#1E293B',
  textMuted: '#64748B',
  textLight: '#94A3B8',
  hala: '#1B3A6B',
  user: '#F1F5F9',
};

const MESSAGE_TYPES = {
  question: { label: 'سؤال', icon: HelpCircle, color: COLORS.info },
  recommendation: { label: 'توصية', icon: Target, color: COLORS.success },
  info: { label: 'معلومة', icon: BookOpen, color: COLORS.gold },
  decision: { label: 'قرار', icon: Zap, color: COLORS.error },
};

const FEEDBACK_TYPES = {
  correct: { label: 'صحيحة', color: COLORS.success, icon: Check },
  partial: { label: 'مقبولة', color: COLORS.warning, icon: AlertCircle },
  wrong: { label: 'خاطئة', color: COLORS.error, icon: XCircle },
};

const ERROR_REASONS = [
  { id: 'intent_wrong', label: 'Intent خطأ' },
  { id: 'need_wrong', label: 'Need خطأ' },
  { id: 'service_wrong', label: 'Service خطأ' },
  { id: 'question_inappropriate', label: 'سؤال غير مناسب' },
  { id: 'missing_context', label: 'سياق مفقود' },
];

const STATE_CONFIG = {
  WELCOME: { label: 'ترحيب', color: COLORS.primary, icon: Clock },
  DISCOVERY: { label: 'استكشاف', color: COLORS.warning, icon: Play },
  REFLECTION: { label: 'انعكاس', color: COLORS.gold, icon: Pause },
  RESULT_READY: { label: 'نتائج جاهزة', color: COLORS.success, icon: CheckCircle },
  PLAN_READY: { label: 'خطة جاهزة', color: COLORS.success, icon: CheckCircle },
  SERVICE_ACTIVE: { label: 'خدمة نشطة', color: COLORS.success, icon: Play },
  SERVICE_IN_PROGRESS: { label: 'قيد التنفيذ', color: COLORS.warning, icon: Clock },
  SERVICE_SUBMITTED: { label: 'خدمة مكتملة', color: COLORS.success, icon: CheckCircle },
  CLIENT_INFO_CAPTURE: { label: 'جمع المعلومات', color: COLORS.warning, icon: User },
};

// ═══════════════════════════════════════════════════════════════════════════════
// CHAT BUBBLE COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

function ChatBubble({ message, onEvaluate, feedback, isLast }) {
  const isHala = message.sender === 'assistant';
  const [showEvaluation, setShowEvaluation] = useState(false);
  
  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleTimeString('ar-SA', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } catch { return ''; }
  };
  
  // Detect message type based on content
  const detectMessageType = (text) => {
    if (!text) return 'info';
    if (text.includes('؟') || text.includes('?')) return 'question';
    if (text.includes('نوصي') || text.includes('أنصح') || text.includes('خطة')) return 'recommendation';
    if (text.includes('قرار') || text.includes('تم') || text.includes('اختر')) return 'decision';
    return 'info';
  };
  
  const msgType = detectMessageType(message.message_text);
  const TypeIcon = MESSAGE_TYPES[msgType]?.icon || BookOpen;
  
  return (
    <div 
      className="chat-bubble-container"
      style={{
        display: 'flex',
        flexDirection: isHala ? 'row' : 'row-reverse',
        gap: '12px',
        marginBottom: '16px',
        animation: isLast ? 'fadeInUp 0.3s ease' : 'none',
      }}
    >
      {/* Avatar */}
      <div style={{
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        backgroundColor: isHala ? COLORS.primary : COLORS.bgDark,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      }}>
        {isHala ? (
          <Sparkles size={18} color={COLORS.gold} />
        ) : (
          <User size={18} color={COLORS.textMuted} />
        )}
      </div>
      
      {/* Message Content */}
      <div style={{ maxWidth: '75%', display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {/* Message Type Tag (for Hala only) */}
        {isHala && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            alignSelf: 'flex-start',
          }}>
            <TypeIcon size={12} color={MESSAGE_TYPES[msgType]?.color} />
            <span style={{ 
              fontSize: '10px', 
              color: MESSAGE_TYPES[msgType]?.color,
              fontWeight: 500,
            }}>
              {MESSAGE_TYPES[msgType]?.label}
            </span>
          </div>
        )}
        
        {/* Bubble */}
        <div style={{
          padding: '14px 18px',
          borderRadius: isHala ? '4px 18px 18px 18px' : '18px 4px 18px 18px',
          backgroundColor: isHala ? COLORS.primary : COLORS.user,
          color: isHala ? COLORS.white : COLORS.text,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          position: 'relative',
        }}>
          <p style={{ 
            margin: 0, 
            fontSize: '14px', 
            lineHeight: 1.7, 
            whiteSpace: 'pre-wrap',
          }}>
            {message.message_text || message.content}
          </p>
          
          {/* Feedback Badge */}
          {feedback && (
            <div style={{
              position: 'absolute',
              top: '-8px',
              left: isHala ? '-8px' : 'auto',
              right: isHala ? 'auto' : '-8px',
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              backgroundColor: FEEDBACK_TYPES[feedback.feedback_type]?.color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              {feedback.feedback_type === 'correct' && <Check size={12} color="white" />}
              {feedback.feedback_type === 'partial' && <AlertCircle size={12} color="white" />}
              {feedback.feedback_type === 'wrong' && <XCircle size={12} color="white" />}
            </div>
          )}
        </div>
        
        {/* Time & Evaluation */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          justifyContent: isHala ? 'flex-start' : 'flex-end',
        }}>
          <span style={{ fontSize: '11px', color: COLORS.textLight }}>
            {formatTime(message.created_at || message.timestamp)}
          </span>
          
          {/* Evaluation Buttons (for Hala messages only) */}
          {isHala && (
            <div style={{ display: 'flex', gap: '4px' }}>
              {!feedback ? (
                <>
                  <button
                    onClick={() => onEvaluate(message, 'correct')}
                    title="صحيحة"
                    style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '4px',
                      border: 'none',
                      backgroundColor: `${COLORS.success}15`,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Check size={14} color={COLORS.success} />
                  </button>
                  <button
                    onClick={() => onEvaluate(message, 'partial')}
                    title="مقبولة"
                    style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '4px',
                      border: 'none',
                      backgroundColor: `${COLORS.warning}15`,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <AlertCircle size={14} color={COLORS.warning} />
                  </button>
                  <button
                    onClick={() => onEvaluate(message, 'wrong')}
                    title="خاطئة"
                    style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '4px',
                      border: 'none',
                      backgroundColor: `${COLORS.error}15`,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <XCircle size={14} color={COLORS.error} />
                  </button>
                </>
              ) : (
                <span style={{
                  fontSize: '11px',
                  color: FEEDBACK_TYPES[feedback.feedback_type]?.color,
                  fontWeight: 500,
                }}>
                  {FEEDBACK_TYPES[feedback.feedback_type]?.label}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ERROR CORRECTION MODAL
// ═══════════════════════════════════════════════════════════════════════════════

function ErrorCorrectionModal({ 
  isOpen, 
  onClose, 
  message, 
  session,
  previousUserMessage,
  onSubmit,
  intents,
  needs,
  services,
  categories
}) {
  const [errorReasons, setErrorReasons] = useState([]);
  const [correctIntent, setCorrectIntent] = useState('');
  const [correctNeed, setCorrectNeed] = useState('');
  const [correctService, setCorrectService] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [notes, setNotes] = useState('');
  const [affectsRule, setAffectsRule] = useState(false);
  const [needsPromptUpdate, setNeedsPromptUpdate] = useState(false);
  const [loading, setLoading] = useState(false);
  
  if (!isOpen || !message) return null;
  
  const filteredServices = selectedCategory 
    ? services.filter(s => s.category_id === selectedCategory)
    : services;
  
  const handleSubmit = async () => {
    setLoading(true);
    try {
      await onSubmit({
        session_id: session.session_id,
        message_id: message.message_id,
        user_input: previousUserMessage?.message_text || '',
        ai_response: message.message_text,
        message_type: 'recommendation',
        detected_intent: session.main_problem,
        detected_need: session.support_type,
        suggested_service: null,
        feedback_type: 'wrong',
        error_reasons: errorReasons,
        correct_intent: correctIntent || null,
        correct_need: correctNeed || null,
        correct_service: correctService || null,
        notes: notes,
        affects_rule: affectsRule,
        needs_prompt_update: needsPromptUpdate,
      });
      onClose();
    } catch (e) {
      console.error('Failed to submit feedback:', e);
    } finally {
      setLoading(false);
    }
  };
  
  const toggleErrorReason = (reasonId) => {
    setErrorReasons(prev => 
      prev.includes(reasonId) 
        ? prev.filter(r => r !== reasonId)
        : [...prev, reasonId]
    );
  };
  
  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999 }}>
      {/* Overlay */}
      <div 
        style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)' }}
        onClick={onClose}
      />
      
      {/* Modal */}
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '600px',
        maxWidth: '95vw',
        maxHeight: '90vh',
        backgroundColor: COLORS.white,
        borderRadius: '16px',
        boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: `1px solid ${COLORS.border}`,
          backgroundColor: COLORS.error,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <XCircle size={24} color="white" />
            <div>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: 'white' }}>
                تصحيح الخطأ
              </h3>
              <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'rgba(255,255,255,0.8)' }}>
                أرسل التصحيح لمركز تعلم هلا
              </p>
            </div>
          </div>
          <button onClick={onClose} style={{
            width: '36px',
            height: '36px',
            borderRadius: '8px',
            border: 'none',
            backgroundColor: 'rgba(255,255,255,0.2)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <X size={18} color="white" />
          </button>
        </div>
        
        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
          {/* Original Message */}
          <div style={{
            backgroundColor: COLORS.bgDark,
            padding: '16px',
            borderRadius: '12px',
            marginBottom: '24px',
          }}>
            <p style={{ fontSize: '12px', color: COLORS.textMuted, margin: '0 0 8px' }}>
              رسالة هلا الخاطئة:
            </p>
            <p style={{ margin: 0, fontSize: '14px', color: COLORS.text, lineHeight: 1.6 }}>
              {message.message_text}
            </p>
          </div>
          
          {/* Error Reasons */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ 
              display: 'block', 
              fontSize: '14px', 
              fontWeight: 600, 
              color: COLORS.text,
              marginBottom: '12px',
            }}>
              سبب الخطأ
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {ERROR_REASONS.map(reason => (
                <button
                  key={reason.id}
                  onClick={() => toggleErrorReason(reason.id)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '8px',
                    border: `2px solid ${errorReasons.includes(reason.id) ? COLORS.error : COLORS.border}`,
                    backgroundColor: errorReasons.includes(reason.id) ? COLORS.errorLight : 'transparent',
                    color: errorReasons.includes(reason.id) ? COLORS.error : COLORS.text,
                    fontSize: '13px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                >
                  {errorReasons.includes(reason.id) && <Check size={14} style={{ marginLeft: '6px' }} />}
                  {reason.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Corrections */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', 
            gap: '16px',
            marginBottom: '24px',
          }}>
            {/* Correct Intent */}
            <div>
              <label style={{ 
                display: 'block', 
                fontSize: '13px', 
                fontWeight: 500, 
                color: COLORS.text,
                marginBottom: '8px',
              }}>
                Intent الصحيح
              </label>
              <select
                value={correctIntent}
                onChange={e => setCorrectIntent(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: `1px solid ${COLORS.border}`,
                  fontSize: '13px',
                  fontFamily: 'inherit',
                  backgroundColor: COLORS.white,
                }}
              >
                <option value="">-- اختر --</option>
                {intents.map(i => (
                  <option key={i.intent_id} value={i.intent_id}>{i.name_ar}</option>
                ))}
              </select>
            </div>
            
            {/* Correct Need */}
            <div>
              <label style={{ 
                display: 'block', 
                fontSize: '13px', 
                fontWeight: 500, 
                color: COLORS.text,
                marginBottom: '8px',
              }}>
                Need الصحيح
              </label>
              <select
                value={correctNeed}
                onChange={e => setCorrectNeed(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: `1px solid ${COLORS.border}`,
                  fontSize: '13px',
                  fontFamily: 'inherit',
                  backgroundColor: COLORS.white,
                }}
              >
                <option value="">-- اختر --</option>
                {needs.map(n => (
                  <option key={n.need_id} value={n.need_id}>{n.name_ar}</option>
                ))}
              </select>
            </div>
            
            {/* Category Filter */}
            <div>
              <label style={{ 
                display: 'block', 
                fontSize: '13px', 
                fontWeight: 500, 
                color: COLORS.text,
                marginBottom: '8px',
              }}>
                فئة الخدمة
              </label>
              <select
                value={selectedCategory}
                onChange={e => { setSelectedCategory(e.target.value); setCorrectService(''); }}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: `1px solid ${COLORS.border}`,
                  fontSize: '13px',
                  fontFamily: 'inherit',
                  backgroundColor: COLORS.white,
                }}
              >
                <option value="">-- كل الفئات --</option>
                {categories.map(c => (
                  <option key={c.category_id} value={c.category_id}>{c.name_ar}</option>
                ))}
              </select>
            </div>
            
            {/* Correct Service */}
            <div>
              <label style={{ 
                display: 'block', 
                fontSize: '13px', 
                fontWeight: 500, 
                color: COLORS.text,
                marginBottom: '8px',
              }}>
                Service الصحيح
              </label>
              <select
                value={correctService}
                onChange={e => setCorrectService(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: `1px solid ${COLORS.border}`,
                  fontSize: '13px',
                  fontFamily: 'inherit',
                  backgroundColor: COLORS.white,
                }}
              >
                <option value="">-- اختر --</option>
                {filteredServices.map(s => (
                  <option key={s.service_id} value={s.service_id}>
                    {s.name_ar} {s.category_name_ar ? `(${s.category_name_ar})` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Notes */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ 
              display: 'block', 
              fontSize: '13px', 
              fontWeight: 500, 
              color: COLORS.text,
              marginBottom: '8px',
            }}>
              ملاحظات إضافية
            </label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="أضف أي ملاحظات تساعد في تحسين النظام..."
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: `1px solid ${COLORS.border}`,
                fontSize: '13px',
                fontFamily: 'inherit',
                resize: 'vertical',
                minHeight: '80px',
              }}
            />
          </div>
          
          {/* Learning Impact */}
          <div style={{
            backgroundColor: COLORS.infoLight,
            padding: '16px',
            borderRadius: '12px',
            borderRight: `4px solid ${COLORS.info}`,
          }}>
            <p style={{ 
              fontSize: '14px', 
              fontWeight: 600, 
              color: COLORS.info,
              margin: '0 0 12px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}>
              <Brain size={16} />
              تأثير التعلم
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                fontSize: '13px',
                color: COLORS.text,
                cursor: 'pointer',
              }}>
                <input
                  type="checkbox"
                  checked={affectsRule}
                  onChange={e => setAffectsRule(e.target.checked)}
                  style={{ width: '16px', height: '16px' }}
                />
                يؤثر على قاعدة التوصيات
              </label>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                fontSize: '13px',
                color: COLORS.text,
                cursor: 'pointer',
              }}>
                <input
                  type="checkbox"
                  checked={needsPromptUpdate}
                  onChange={e => setNeedsPromptUpdate(e.target.checked)}
                  style={{ width: '16px', height: '16px' }}
                />
                يحتاج تحديث Prompt
              </label>
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: `1px solid ${COLORS.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              border: `1px solid ${COLORS.border}`,
              backgroundColor: 'transparent',
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            إلغاء
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || errorReasons.length === 0}
            style={{
              padding: '10px 24px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: loading || errorReasons.length === 0 ? COLORS.bgDark : COLORS.primary,
              color: loading || errorReasons.length === 0 ? COLORS.textMuted : COLORS.white,
              fontSize: '14px',
              fontWeight: 600,
              cursor: loading || errorReasons.length === 0 ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <Send size={16} />
            حفظ وإرسال لمركز التعلم
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SESSION INTELLIGENCE PANEL
// ═══════════════════════════════════════════════════════════════════════════════

function SessionIntelligencePanel({ intelligence }) {
  if (!intelligence) return null;
  
  const { metrics, flags, detected_intent, confidence_score } = intelligence;
  
  return (
    <div style={{
      backgroundColor: COLORS.white,
      borderRadius: '12px',
      border: `1px solid ${COLORS.border}`,
      padding: '16px',
      marginBottom: '16px',
    }}>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '8px',
        marginBottom: '16px',
      }}>
        <Brain size={18} color={COLORS.primary} />
        <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: COLORS.text }}>
          ذكاء الجلسة
        </h3>
      </div>
      
      {/* Metrics Grid */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(4, 1fr)', 
        gap: '12px',
        marginBottom: '16px',
      }}>
        <div style={{
          backgroundColor: COLORS.bg,
          padding: '12px',
          borderRadius: '8px',
          textAlign: 'center',
        }}>
          <p style={{ fontSize: '11px', color: COLORS.textMuted, margin: '0 0 4px' }}>
            Intent المكتشف
          </p>
          <p style={{ 
            fontSize: '13px', 
            fontWeight: 600, 
            color: detected_intent ? COLORS.success : COLORS.error,
            margin: 0,
          }}>
            {detected_intent ? '✅' : '❌'} {detected_intent || 'غير محدد'}
          </p>
        </div>
        
        <div style={{
          backgroundColor: COLORS.bg,
          padding: '12px',
          borderRadius: '8px',
          textAlign: 'center',
        }}>
          <p style={{ fontSize: '11px', color: COLORS.textMuted, margin: '0 0 4px' }}>
            دقة التقييم
          </p>
          <p style={{ 
            fontSize: '18px', 
            fontWeight: 700, 
            color: metrics.accuracy_rate >= 80 ? COLORS.success : metrics.accuracy_rate >= 50 ? COLORS.warning : COLORS.error,
            margin: 0,
          }}>
            {metrics.accuracy_rate}%
          </p>
        </div>
        
        <div style={{
          backgroundColor: COLORS.bg,
          padding: '12px',
          borderRadius: '8px',
          textAlign: 'center',
        }}>
          <p style={{ fontSize: '11px', color: COLORS.textMuted, margin: '0 0 4px' }}>
            مستوى الثقة
          </p>
          <p style={{ 
            fontSize: '18px', 
            fontWeight: 700, 
            color: confidence_score >= 80 ? COLORS.success : confidence_score >= 60 ? COLORS.warning : COLORS.error,
            margin: 0,
          }}>
            {confidence_score}%
          </p>
        </div>
        
        <div style={{
          backgroundColor: COLORS.bg,
          padding: '12px',
          borderRadius: '8px',
          textAlign: 'center',
        }}>
          <p style={{ fontSize: '11px', color: COLORS.textMuted, margin: '0 0 4px' }}>
            تم تقييمها
          </p>
          <p style={{ 
            fontSize: '18px', 
            fontWeight: 700, 
            color: COLORS.primary,
            margin: 0,
          }}>
            {metrics.evaluated_messages}/{metrics.ai_messages}
          </p>
        </div>
      </div>
      
      {/* Flags */}
      {flags && flags.length > 0 && (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {flags.map((flag, idx) => (
            <span key={idx} style={{
              padding: '4px 10px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: 500,
              backgroundColor: flag.type === 'error' ? COLORS.errorLight 
                : flag.type === 'warning' ? COLORS.warningLight 
                : COLORS.infoLight,
              color: flag.type === 'error' ? COLORS.error 
                : flag.type === 'warning' ? COLORS.warning 
                : COLORS.info,
            }}>
              {flag.type === 'error' && '❌'} 
              {flag.type === 'warning' && '⚠️'} 
              {flag.type === 'info' && 'ℹ️'} 
              {flag.label} ({flag.count})
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SESSION DRAWER (Main Component)
// ═══════════════════════════════════════════════════════════════════════════════

function SessionDrawer({ session, isOpen, onClose, onRefresh }) {
  const [activeTab, setActiveTab] = useState('chat');
  const [intelligence, setIntelligence] = useState(null);
  const [feedbacks, setFeedbacks] = useState({});
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [intents, setIntents] = useState([]);
  const [needs, setNeeds] = useState([]);
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loadingIntelligence, setLoadingIntelligence] = useState(false);
  const [decisionTrace, setDecisionTrace] = useState([]);
  const chatEndRef = useRef(null);
  
  useEffect(() => {
    if (isOpen && session) {
      fetchIntelligence();
      fetchDropdownData();
      fetchDecisionTrace();
    }
  }, [isOpen, session?.session_id]);
  
  useEffect(() => {
    // Build feedbacks map from intelligence data
    if (intelligence?.feedbacks) {
      const map = {};
      intelligence.feedbacks.forEach(fb => {
        map[fb.message_id] = fb;
      });
      setFeedbacks(map);
    }
  }, [intelligence]);
  
  const fetchIntelligence = async () => {
    if (!session?.session_id) return;
    setLoadingIntelligence(true);
    try {
      const res = await axios.get(
        `${API}/learning/session/${session.session_id}/intelligence`,
        { headers: getAuthHeaders() }
      );
      setIntelligence(res.data);
    } catch (e) {
      console.error('Failed to fetch intelligence:', e);
    } finally {
      setLoadingIntelligence(false);
    }
  };
  
  const fetchDecisionTrace = async () => {
    if (!session?.session_id) return;
    try {
      const res = await axios.get(
        `${API}/learning/session/${session.session_id}/decision-trace`,
        { headers: getAuthHeaders() }
      );
      setDecisionTrace(res.data || []);
    } catch (e) {
      console.error('Failed to fetch decision trace:', e);
    }
  };
  
  const fetchDropdownData = async () => {
    try {
      const [intentsRes, needsRes, servicesRes, catsRes] = await Promise.all([
        axios.get(`${API}/learning/intents`, { headers: getAuthHeaders() }),
        axios.get(`${API}/learning/needs`, { headers: getAuthHeaders() }),
        axios.get(`${API}/learning/services`, { headers: getAuthHeaders() }),
        axios.get(`${API}/learning/categories`, { headers: getAuthHeaders() }),
      ]);
      setIntents(intentsRes.data || []);
      setNeeds(needsRes.data || []);
      setServices(servicesRes.data || []);
      setCategories(catsRes.data || []);
    } catch (e) {
      console.error('Failed to fetch dropdown data:', e);
    }
  };
  
  const handleEvaluate = async (message, feedbackType) => {
    if (feedbackType === 'wrong') {
      // Find previous user message
      const msgIdx = session.conversation?.findIndex(m => m.message_id === message.message_id);
      const prevUserMsg = msgIdx > 0 
        ? session.conversation.slice(0, msgIdx).reverse().find(m => m.sender === 'user')
        : null;
      setSelectedMessage({ ...message, prevUserMsg });
      setShowErrorModal(true);
    } else {
      // Direct evaluation (correct/partial)
      try {
        const msgIdx = session.conversation?.findIndex(m => m.message_id === message.message_id);
        const prevUserMsg = msgIdx > 0 
          ? session.conversation.slice(0, msgIdx).reverse().find(m => m.sender === 'user')
          : null;
          
        await axios.post(`${API}/learning/feedback`, {
          session_id: session.session_id,
          message_id: message.message_id,
          user_input: prevUserMsg?.message_text || '',
          ai_response: message.message_text,
          feedback_type: feedbackType,
          detected_intent: session.main_problem,
          detected_need: session.support_type,
        }, { headers: getAuthHeaders() });
        
        // Refresh intelligence
        await fetchIntelligence();
      } catch (e) {
        console.error('Failed to submit feedback:', e);
      }
    }
  };
  
  const handleErrorSubmit = async (data) => {
    try {
      await axios.post(`${API}/learning/feedback`, data, { headers: getAuthHeaders() });
      setShowErrorModal(false);
      setSelectedMessage(null);
      await fetchIntelligence();
    } catch (e) {
      console.error('Failed to submit error feedback:', e);
      throw e;
    }
  };
  
  if (!isOpen || !session) return null;
  
  const status = STATE_CONFIG[session.session_status] || STATE_CONFIG.WELCOME;
  const StatusIcon = status.icon;
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString('ar-SA', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch { return dateStr; }
  };
  
  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 9998 }}>
      {/* Overlay */}
      <div 
        style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)' }}
        onClick={onClose}
      />
      
      {/* Drawer */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        bottom: 0,
        width: '850px',
        maxWidth: '95vw',
        backgroundColor: COLORS.bg,
        boxShadow: '4px 0 30px rgba(0,0,0,0.2)',
        display: 'flex',
        flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: `1px solid ${COLORS.border}`,
          backgroundColor: COLORS.primary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              backgroundColor: 'rgba(255,255,255,0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Brain size={24} color={COLORS.gold} />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: COLORS.white }}>
                تحليل الجلسة
              </h2>
              <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>
                {session.client_name || 'مستخدم'} • {session.session_id?.substring(0, 16)}...
              </p>
            </div>
          </div>
          <button onClick={onClose} style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            border: 'none',
            backgroundColor: 'rgba(255,255,255,0.15)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background 0.2s',
          }}
          onMouseEnter={e => e.target.style.backgroundColor = 'rgba(255,255,255,0.25)'}
          onMouseLeave={e => e.target.style.backgroundColor = 'rgba(255,255,255,0.15)'}
          >
            <X size={20} color={COLORS.white} />
          </button>
        </div>
        
        {/* Session Intelligence Panel */}
        <div style={{ padding: '20px 24px', backgroundColor: COLORS.bg, flexShrink: 0 }}>
          {loadingIntelligence ? (
            <div style={{ textAlign: 'center', padding: '20px', color: COLORS.textMuted }}>
              جاري تحميل البيانات...
            </div>
          ) : (
            <SessionIntelligencePanel intelligence={intelligence} />
          )}
          
          {/* Quick Info - More spacious grid */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(4, 1fr)', 
            gap: '12px',
            marginTop: '16px',
          }}>
            <div style={{
              backgroundColor: COLORS.white,
              padding: '14px 16px',
              borderRadius: '10px',
              border: `1px solid ${COLORS.border}`,
            }}>
              <p style={{ fontSize: '11px', color: COLORS.textMuted, margin: '0 0 6px' }}>الحالة</p>
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '5px 10px',
                borderRadius: '6px',
                backgroundColor: `${status.color}15`,
                color: status.color,
                fontSize: '12px',
                fontWeight: 500,
              }}>
                <StatusIcon size={12} />
                {status.label}
              </span>
            </div>
            <div style={{
              backgroundColor: COLORS.white,
              padding: '14px 16px',
              borderRadius: '10px',
              border: `1px solid ${COLORS.border}`,
            }}>
              <p style={{ fontSize: '11px', color: COLORS.textMuted, margin: '0 0 6px' }}>العميل</p>
              <p style={{ fontSize: '14px', fontWeight: 500, color: COLORS.text, margin: 0 }}>
                {session.client_name || 'غير معروف'}
              </p>
            </div>
            <div style={{
              backgroundColor: COLORS.white,
              padding: '14px 16px',
              borderRadius: '10px',
              border: `1px solid ${COLORS.border}`,
            }}>
              <p style={{ fontSize: '11px', color: COLORS.textMuted, margin: '0 0 6px' }}>نوع المستخدم</p>
              <p style={{ fontSize: '13px', fontWeight: 500, color: COLORS.text, margin: 0 }}>
                {session.user_type || 'غير محدد'}
              </p>
            </div>
            <div style={{
              backgroundColor: COLORS.white,
              padding: '14px 16px',
              borderRadius: '10px',
              border: `1px solid ${COLORS.border}`,
            }}>
              <p style={{ fontSize: '11px', color: COLORS.textMuted, margin: '0 0 6px' }}>التاريخ</p>
              <p style={{ fontSize: '13px', fontWeight: 500, color: COLORS.text, margin: 0 }}>
                {formatDate(session.created_at)}
              </p>
            </div>
          </div>
        </div>
        
        {/* Tabs */}
        <div style={{ 
          display: 'flex', 
          borderBottom: `1px solid ${COLORS.border}`,
          flexShrink: 0,
          backgroundColor: COLORS.white,
        }}>
          {[
            { id: 'chat', label: 'المحادثة', icon: MessageSquare },
            { id: 'trace', label: 'تتبع القرارات', icon: Target },
          ].map(tab => {
            const TabIcon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  flex: 1,
                  padding: '14px',
                  border: 'none',
                  backgroundColor: activeTab === tab.id ? COLORS.bg : 'transparent',
                  borderBottom: activeTab === tab.id ? `3px solid ${COLORS.primary}` : '3px solid transparent',
                  color: activeTab === tab.id ? COLORS.primary : COLORS.textMuted,
                  fontSize: '14px',
                  fontWeight: activeTab === tab.id ? 600 : 500,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'all 0.2s',
                }}
              >
                <TabIcon size={18} />
                {tab.label}
              </button>
            );
          })}
        </div>
        
        {/* Tab Content */}
        <div style={{ 
          flex: 1, 
          overflowY: 'auto', 
          padding: '24px',
          backgroundColor: COLORS.white,
        }}>
          {activeTab === 'chat' ? (
            <div style={{ maxWidth: '700px', margin: '0 auto' }}>
              {(!session.conversation || session.conversation.length === 0) ? (
                <div style={{ textAlign: 'center', padding: '60px 20px', color: COLORS.textMuted }}>
                  <MessageSquare size={48} style={{ marginBottom: '16px', opacity: 0.4 }} />
                  <p style={{ margin: 0, fontSize: '16px' }}>لا توجد رسائل في هذه الجلسة</p>
                </div>
              ) : (
                <>
                  {session.conversation.map((msg, idx) => (
                    <ChatBubble
                      key={msg.message_id || idx}
                      message={msg}
                      onEvaluate={handleEvaluate}
                      feedback={feedbacks[msg.message_id]}
                      isLast={idx === session.conversation.length - 1}
                    />
                  ))}
                  <div ref={chatEndRef} />
                </>
              )}
            </div>
          ) : (
            <div style={{ maxWidth: '700px', margin: '0 auto' }}>
              {/* Decision Trace */}
              {decisionTrace.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {decisionTrace.filter(t => t.is_recommendation).map((trace, idx) => (
                    <div key={idx} style={{
                      backgroundColor: COLORS.bg,
                      borderRadius: '16px',
                      padding: '20px',
                      borderRight: `4px solid ${COLORS.primary}`,
                    }}>
                      {/* Message Preview */}
                      <p style={{ 
                        margin: '0 0 12px', 
                        fontSize: '13px', 
                        color: COLORS.text,
                        backgroundColor: COLORS.white,
                        padding: '10px 12px',
                        borderRadius: '8px',
                      }}>
                        {trace.message_text}
                      </p>
                      
                      {/* Trace Details */}
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(2, 1fr)', 
                        gap: '10px',
                        marginBottom: '12px',
                      }}>
                        <div style={{
                          backgroundColor: COLORS.white,
                          padding: '10px 12px',
                          borderRadius: '8px',
                        }}>
                          <p style={{ fontSize: '10px', color: COLORS.textMuted, margin: '0 0 4px' }}>
                            Intent المكتشف
                          </p>
                          <p style={{ fontSize: '12px', fontWeight: 500, color: COLORS.primary, margin: 0 }}>
                            {trace.detected_intent || 'غير محدد'}
                          </p>
                        </div>
                        <div style={{
                          backgroundColor: COLORS.white,
                          padding: '10px 12px',
                          borderRadius: '8px',
                        }}>
                          <p style={{ fontSize: '10px', color: COLORS.textMuted, margin: '0 0 4px' }}>
                            Need المطبق
                          </p>
                          <p style={{ fontSize: '12px', fontWeight: 500, color: COLORS.primary, margin: 0 }}>
                            {trace.detected_need || 'غير محدد'}
                          </p>
                        </div>
                      </div>
                      
                      {/* Applied Rules */}
                      {trace.applied_rules?.length > 0 && (
                        <div style={{ marginBottom: '12px' }}>
                          <p style={{ fontSize: '11px', color: COLORS.textMuted, margin: '0 0 6px' }}>
                            القواعد المطبقة:
                          </p>
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            {trace.applied_rules.map((rule, rIdx) => (
                              <span key={rIdx} style={{
                                padding: '4px 8px',
                                borderRadius: '4px',
                                backgroundColor: COLORS.infoLight,
                                color: COLORS.info,
                                fontSize: '11px',
                              }}>
                                {rule.rule_id} (P:{rule.priority})
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Suggested Services */}
                      {trace.suggested_services?.length > 0 && (
                        <div style={{ marginBottom: '12px' }}>
                          <p style={{ fontSize: '11px', color: COLORS.textMuted, margin: '0 0 6px' }}>
                            الخدمات المقترحة:
                          </p>
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            {trace.suggested_services.map((svc, sIdx) => (
                              <span key={sIdx} style={{
                                padding: '4px 8px',
                                borderRadius: '4px',
                                backgroundColor: COLORS.successLight,
                                color: COLORS.success,
                                fontSize: '11px',
                              }}>
                                {svc}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Confidence Factors */}
                      {trace.confidence_factors?.length > 0 && (
                        <div>
                          <p style={{ fontSize: '11px', color: COLORS.textMuted, margin: '0 0 6px' }}>
                            عوامل الثقة:
                          </p>
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            {trace.confidence_factors.map((factor, fIdx) => (
                              <span key={fIdx} style={{
                                padding: '4px 8px',
                                borderRadius: '4px',
                                backgroundColor: COLORS.goldLight || '#FEF3C7',
                                color: COLORS.gold,
                                fontSize: '10px',
                              }}>
                                {factor.label} (+{Math.round(factor.boost * 100)}%)
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{
                  backgroundColor: COLORS.bg,
                  padding: '40px 20px',
                  borderRadius: '12px',
                  textAlign: 'center',
                }}>
                  <Target size={40} color={COLORS.textMuted} style={{ marginBottom: '12px', opacity: 0.5 }} />
                  <p style={{ margin: 0, fontSize: '14px', color: COLORS.textMuted }}>
                    لا توجد توصيات في هذه الجلسة بعد
                  </p>
                  <p style={{ margin: '8px 0 0', fontSize: '12px', color: COLORS.textLight }}>
                    سيظهر تتبع القرارات عند وصول الجلسة لمرحلة التوصيات
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Error Correction Modal */}
      <ErrorCorrectionModal
        isOpen={showErrorModal}
        onClose={() => { setShowErrorModal(false); setSelectedMessage(null); }}
        message={selectedMessage}
        session={session}
        previousUserMessage={selectedMessage?.prevUserMsg}
        onSubmit={handleErrorSubmit}
        intents={intents}
        needs={needs}
        services={services}
        categories={categories}
      />
      
      {/* CSS Animations */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>,
    document.body
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SESSIONS PAGE (Main Export)
// ═══════════════════════════════════════════════════════════════════════════════

export default function SessionsPage() {
  const [sessions, setSessions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedSession, setSelectedSession] = useState(null);
  const [showDrawer, setShowDrawer] = useState(false);
  const [page, setPage] = useState(0);
  const [learningStats, setLearningStats] = useState(null);
  const limit = 20;

  useEffect(() => {
    fetchSessions();
    fetchLearningStats();
  }, [searchQuery, statusFilter, page]);

  const fetchSessions = async () => {
    setLoading(true);
    try {
      let url = `${API}/control-center/sessions?skip=${page * limit}&limit=${limit}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      if (statusFilter) url += `&status=${statusFilter}`;
      
      const res = await axios.get(url, { headers: getAuthHeaders() });
      setSessions(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      console.error('Failed to fetch sessions:', e);
      if (e.response?.status === 401) {
        window.location.href = '/admin/login';
      }
    } finally {
      setLoading(false);
    }
  };
  
  const fetchLearningStats = async () => {
    try {
      const res = await axios.get(`${API}/learning/stats`, { headers: getAuthHeaders() });
      setLearningStats(res.data);
    } catch (e) {
      console.error('Failed to fetch learning stats:', e);
    }
  };

  const handleSessionClick = async (session) => {
    // Navigate to full session analysis page
    window.location.href = `/admin/session/${session.session_id}`;
  };

  const handleQuickView = async (session, e) => {
    // Prevent navigation when clicking quick view button
    e.stopPropagation();
    try {
      const res = await axios.get(
        `${API}/control-center/sessions/${session.session_id}`, 
        { headers: getAuthHeaders() }
      );
      setSelectedSession(res.data);
      setShowDrawer(true);
    } catch (e) {
      console.error('Failed to fetch session details:', e);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString('ar-SA', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch { return dateStr; }
  };

  return (
    <div>
      {/* Page Header */}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: COLORS.text, margin: '0 0 8px' }}>
            الجلسات
          </h1>
          <p style={{ fontSize: '14px', color: COLORS.textMuted, margin: 0 }}>
            تحليل وتقييم جلسات المستخدمين • إجمالي {total} جلسة
          </p>
        </div>
        <button 
          onClick={() => { fetchSessions(); fetchLearningStats(); }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            borderRadius: '8px',
            border: `1px solid ${COLORS.border}`,
            backgroundColor: COLORS.white,
            fontSize: '13px',
            cursor: 'pointer'
          }}
        >
          <RefreshCw size={16} color={COLORS.textMuted} />
          تحديث
        </button>
      </div>

      {/* Introduction Banner */}
      <div style={{
        backgroundColor: `${COLORS.gold}12`,
        borderRadius: '16px',
        padding: '20px 24px',
        marginBottom: '24px',
        borderRight: `4px solid ${COLORS.gold}`,
      }}>
        <h3 style={{ margin: '0 0 8px', fontSize: '15px', fontWeight: 600, color: COLORS.text }}>
          ما هي هذه الصفحة؟
        </h3>
        <p style={{ margin: 0, fontSize: '14px', color: COLORS.textMuted, lineHeight: 1.7 }}>
          استعرض جميع محادثات المستخدمين مع هلا. اضغط على أي جلسة لمشاهدة <strong>المحادثة الكاملة</strong>، 
          <strong> تقييم ردود هلا</strong> (صحيحة/مقبولة/خاطئة)، و<strong>تتبع قرارات</strong> التوصية. 
          كل تقييم تضيفه يُرسل تلقائياً لمركز التعلم لتحسين النظام.
        </p>
      </div>
      
      {/* Learning Stats Banner */}
      {learningStats && (
        <div style={{
          backgroundColor: COLORS.primary,
          borderRadius: '16px',
          padding: '20px 24px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              backgroundColor: 'rgba(255,255,255,0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Brain size={24} color={COLORS.gold} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: COLORS.white }}>
                نظام التعلم الذكي
              </h3>
              <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'rgba(255,255,255,0.7)' }}>
                {learningStats.total_feedbacks} تقييم • {learningStats.accuracy_rate}% دقة
              </p>
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: '24px' }}>
            <div style={{ textAlign: 'center' }}>
              <p style={{ fontSize: '24px', fontWeight: 700, color: COLORS.white, margin: 0 }}>
                {learningStats.by_status?.pending || 0}
              </p>
              <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.7)', margin: '4px 0 0' }}>
                بانتظار المراجعة
              </p>
            </div>
            <div style={{ textAlign: 'center' }}>
              <p style={{ fontSize: '24px', fontWeight: 700, color: COLORS.gold, margin: 0 }}>
                {learningStats.by_status?.approved || 0}
              </p>
              <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.7)', margin: '4px 0 0' }}>
                تم الاعتماد
              </p>
            </div>
            <div style={{ textAlign: 'center' }}>
              <p style={{ fontSize: '24px', fontWeight: 700, color: COLORS.success, margin: 0 }}>
                {learningStats.by_status?.applied || 0}
              </p>
              <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.7)', margin: '4px 0 0' }}>
                تم التطبيق
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', 
        gap: '12px',
        marginBottom: '20px'
      }}>
        {Object.entries(STATE_CONFIG).slice(0, 5).map(([key, config]) => {
          const count = sessions.filter(s => s.session_status === key).length;
          const Icon = config.icon;
          return (
            <div 
              key={key}
              onClick={() => setStatusFilter(statusFilter === key ? '' : key)}
              style={{
                backgroundColor: statusFilter === key ? `${config.color}10` : COLORS.white,
                padding: '14px',
                borderRadius: '10px',
                border: `1px solid ${statusFilter === key ? config.color : COLORS.border}`,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                <Icon size={14} color={config.color} />
                <span style={{ fontSize: '11px', color: COLORS.textMuted }}>{config.label}</span>
              </div>
              <p style={{ fontSize: '20px', fontWeight: 'bold', color: config.color, margin: 0 }}>{count}</p>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div style={{
        backgroundColor: COLORS.white,
        borderRadius: '10px',
        padding: '14px 16px',
        marginBottom: '16px',
        border: `1px solid ${COLORS.border}`,
        display: 'flex',
        gap: '12px',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          backgroundColor: COLORS.bg,
          padding: '8px 12px',
          borderRadius: '8px',
          flex: 1,
          minWidth: '250px'
        }}>
          <Search size={16} color={COLORS.textMuted} />
          <input
            type="text"
            placeholder="بحث بالاسم أو الجوال أو رقم الجلسة..."
            value={searchQuery}
            onChange={e => { setSearchQuery(e.target.value); setPage(0); }}
            style={{
              border: 'none',
              backgroundColor: 'transparent',
              outline: 'none',
              flex: 1,
              fontSize: '13px',
              fontFamily: 'inherit'
            }}
          />
        </div>
        
        <select 
          value={statusFilter} 
          onChange={e => { setStatusFilter(e.target.value); setPage(0); }}
          style={{
            padding: '8px 12px',
            borderRadius: '8px',
            border: `1px solid ${COLORS.border}`,
            backgroundColor: COLORS.white,
            fontSize: '13px',
            fontFamily: 'inherit',
            minWidth: '140px'
          }}
        >
          <option value="">كل الحالات</option>
          {Object.entries(STATE_CONFIG).map(([key, config]) => (
            <option key={key} value={key}>{config.label}</option>
          ))}
        </select>
      </div>

      {/* Sessions Table */}
      <div style={{
        backgroundColor: COLORS.white,
        borderRadius: '12px',
        border: `1px solid ${COLORS.border}`,
        overflow: 'hidden'
      }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: COLORS.bg }}>
                <th style={{ padding: '12px 14px', textAlign: 'right', fontSize: '12px', fontWeight: 600, color: COLORS.textMuted }}>العميل</th>
                <th style={{ padding: '12px 14px', textAlign: 'right', fontSize: '12px', fontWeight: 600, color: COLORS.textMuted }}>الحالة</th>
                <th style={{ padding: '12px 14px', textAlign: 'right', fontSize: '12px', fontWeight: 600, color: COLORS.textMuted }}>المشكلة</th>
                <th style={{ padding: '12px 14px', textAlign: 'right', fontSize: '12px', fontWeight: 600, color: COLORS.textMuted }}>الرسائل</th>
                <th style={{ padding: '12px 14px', textAlign: 'right', fontSize: '12px', fontWeight: 600, color: COLORS.textMuted }}>التاريخ</th>
                <th style={{ padding: '12px 14px', width: '50px' }}></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" style={{ padding: '50px', textAlign: 'center', color: COLORS.textMuted }}>
                    جاري التحميل...
                  </td>
                </tr>
              ) : sessions.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ padding: '50px', textAlign: 'center', color: COLORS.textMuted }}>
                    <MessageSquare size={36} style={{ marginBottom: '10px', opacity: 0.4 }} />
                    <p style={{ margin: 0 }}>لا توجد جلسات</p>
                  </td>
                </tr>
              ) : (
                sessions.map(session => {
                  const status = STATE_CONFIG[session.session_status] || STATE_CONFIG.WELCOME;
                  const StatusIcon = status.icon;
                  
                  return (
                    <tr 
                      key={session.session_id} 
                      data-testid={`session-row-${session.session_id}`}
                      style={{ borderBottom: `1px solid ${COLORS.border}`, cursor: 'pointer' }}
                      onClick={() => handleSessionClick(session)}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = COLORS.bg}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <td style={{ padding: '14px' }}>
                        <p style={{ margin: 0, fontSize: '13px', fontWeight: 500, color: COLORS.text }}>
                          {session.client_name || 'مستخدم جديد'}
                        </p>
                        <p style={{ margin: '2px 0 0', fontSize: '11px', color: COLORS.textMuted }}>
                          {session.session_id?.substring(0, 12)}...
                        </p>
                      </td>
                      <td style={{ padding: '14px' }}>
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '4px 8px',
                          borderRadius: '6px',
                          backgroundColor: `${status.color}15`,
                          color: status.color,
                          fontSize: '11px',
                          fontWeight: 500
                        }}>
                          <StatusIcon size={12} />
                          {status.label}
                        </span>
                      </td>
                      <td style={{ padding: '14px' }}>
                        <span style={{ fontSize: '12px', color: COLORS.textMuted }}>
                          {session.main_problem || '-'}
                        </span>
                      </td>
                      <td style={{ padding: '14px' }}>
                        <span style={{ 
                          fontSize: '13px', 
                          fontWeight: 600, 
                          color: session.message_count > 0 ? COLORS.primary : COLORS.textLight 
                        }}>
                          {session.message_count || 0}
                        </span>
                      </td>
                      <td style={{ padding: '14px' }}>
                        <span style={{ fontSize: '11px', color: COLORS.textLight }}>
                          {formatDate(session.created_at)}
                        </span>
                      </td>
                      <td style={{ padding: '14px' }}>
                        <button style={{
                          width: '30px',
                          height: '30px',
                          borderRadius: '6px',
                          border: 'none',
                          backgroundColor: COLORS.bg,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <Eye size={14} color={COLORS.textMuted} />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {total > limit && (
          <div style={{ 
            padding: '14px 16px', 
            borderTop: `1px solid ${COLORS.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <span style={{ fontSize: '12px', color: COLORS.textMuted }}>
              عرض {page * limit + 1} - {Math.min((page + 1) * limit, total)} من {total}
            </span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: `1px solid ${COLORS.border}`,
                  backgroundColor: COLORS.white,
                  fontSize: '12px',
                  cursor: page === 0 ? 'not-allowed' : 'pointer',
                  opacity: page === 0 ? 0.5 : 1
                }}
              >
                السابق
              </button>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={(page + 1) * limit >= total}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: `1px solid ${COLORS.border}`,
                  backgroundColor: COLORS.white,
                  fontSize: '12px',
                  cursor: (page + 1) * limit >= total ? 'not-allowed' : 'pointer',
                  opacity: (page + 1) * limit >= total ? 0.5 : 1
                }}
              >
                التالي
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Session Drawer */}
      <SessionDrawer
        session={selectedSession}
        isOpen={showDrawer}
        onClose={() => { setShowDrawer(false); setSelectedSession(null); }}
        onRefresh={fetchSessions}
      />
    </div>
  );
}
